import { ChevronRightIcon } from "@chakra-ui/icons";
import { Box, Flex } from "@chakra-ui/layout";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/react";
import { AllocationBasisPreviewTable } from "features/fund-family/setup/fund-structure/AllocationBasisPreviewTable";
import { AllocationRuleGraphFormFields } from "features/fund-family/setup/fund-structure/AllocationRuleGraphFormFields";
import { CustomAllocationRuleInputModal } from "features/fund-family/setup/fund-structure/CustomAllocationRuleInputModal";
import { useAllocationRuleGraphForm } from "features/fund-family/setup/fund-structure/hooks/useAllocationRuleGraphForm";
import OptOutsModal from "features/fund-family/setup/fund-structure/OptOutsModal";
import {
  getAllocationRuleGraphAndUseRetrieveQueryKey,
  useAllocationRuleGraphAndUseRetrieveSuspense,
} from "gen/endpoints/allocation-rule-graph/allocation-rule-graph";
import {
  getEqualizationRetrieveQueryKey,
  useEqualizationCreate,
} from "gen/endpoints/equalization/equalization";
import { FundFamilySubEventDataId } from "gen/model/fundFamilySubEventDataId";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
} from "shared/components/content/ContentBlock";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
  FormFooterBreadcrumb,
} from "shared/components/form/FormFooter";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormTextArea } from "shared/components/input/text/TextArea";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useToast } from "shared/components/toast/useToast";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";
import { useBoolean } from "usehooks-ts";

export const AllocationRuleGraphEditPage: React.FC = () => {
  const { fundFamilyId, ruleGraphId, useId } = useRequiredStringParams();
  const { t } = useTranslation(["allocation"]);
  const navigate = useNavigate();
  const { showErrorToast } = useToast();
  const [equalizationSubEventId, setEqualizationSubEventId] = React.useState<
    FundFamilySubEventDataId | undefined
  >();
  const { mutateAsync: createEqualization } = useEqualizationCreate({
    mutation: {
      onError: showErrorToast,
    },
  });

  const invalidateQueries = useInvalidateQueryKeys();

  const toDetailsPage = useCallback(() => {
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .setup({})
        .fundStructure({})
        .fundFamilySetupAllocationRuleGraphAndUse({
          allocationRuleGraphId: ruleGraphId,
          allocationRuleGraphUseId: useId,
        }).$,
    );
  }, [fundFamilyId, navigate, ruleGraphId, useId]);

  const onMoveToEqualization = useCallback(
    async (ruleGraphId: string, useId: string, fundFamilyEventId: string) => {
      // invalidate any previous equalization get queries
      if (equalizationSubEventId) {
        await invalidateQueries(
          getEqualizationRetrieveQueryKey(
            fundFamilyId,
            fundFamilyEventId,
            equalizationSubEventId,
          ),
          getAllocationRuleGraphAndUseRetrieveQueryKey(
            fundFamilyId,
            ruleGraphId,
            useId,
          ),
        );
      }
      // create equalization
      const subEventId = (
        await createEqualization({
          fundFamilyId,
          fundFamilyEventId,
          data: {
            opt_out_of_equalization_interest: true,
          },
        })
      ).id;
      setEqualizationSubEventId(subEventId);

      // then navigate to get preview it if successful
      if (subEventId) {
        navigate(
          rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .setup({})
            .fundStructure({})
            .fundFamilySetupAllocationRuleGraphEqualizationPreview({
              ruleGraphId,
              useId,
              fundFamilyEventId,
              fundFamilySubEventId: subEventId,
            }).$,
          {},
        );
      }
    },
    [
      navigate,
      fundFamilyId,
      createEqualization,
      equalizationSubEventId,
      invalidateQueries,
    ],
  );

  const { data: currentData } = useAllocationRuleGraphAndUseRetrieveSuspense(
    fundFamilyId,
    ruleGraphId,
    useId,
  );

  const {
    form,
    ruleGraphData,
    upsertDraftGraphAndUse,
    onSaveEdgeWeightOverrides,
    toFundStructurePage,
    onCancel,
    isCustomRuleGraph,
    shouldRefresh,
  } = useAllocationRuleGraphForm(
    fundFamilyId,
    true,
    {
      name: currentData.name,
      transactionCodeId: currentData.transaction_code_id ?? undefined,
      investmentId: currentData.investment_id ?? undefined,
      calculationId: currentData.calculation_id,
      edgeOverrideAmounts:
        currentData.security_ownership_allocation_overrides.map((override) => ({
          securityOwnershipId: override.security_ownership_id!,
          overrideAmount: override.override_amount,
        })),
    },
    {
      graphId: currentData.allocation_rule_graph_id,
      graphVersionId: currentData.latest_version_id,
      graphUseId: currentData.allocation_rule_graph_use_id,
      effectiveDate: currentData.effective_date,
      graphfundFamilyEventId: undefined,
      useFundFamilyEventId: undefined,
    },
    toDetailsPage,
  );

  const onSubmit = useCallback(async () => {
    await onMoveToEqualization(
      ruleGraphData!.graphId,
      ruleGraphData!.graphUseId,
      ruleGraphData!.graphfundFamilyEventId!, // TBD - we may need to separate out rule graph edits by use + graph itself
    );
  }, [ruleGraphData, onMoveToEqualization]);

  const {
    value: isOptOutsModalOpen,
    setFalse: onOptOutsModalClose,
    setTrue: onOptOutsModalOpen,
  } = useBoolean(false);

  const {
    value: isCustomRuleModalOpen,
    setFalse: onCustomRuleModalClose,
    setTrue: onCustomRuleModalOpen,
  } = useBoolean(false);

  return (
    <>
      {ruleGraphData && (
        <OptOutsModal
          isOpen={isOptOutsModalOpen}
          onModalClose={onOptOutsModalClose}
          allocationRuleGraphVersionId={ruleGraphData.graphVersionId}
          onSaveOptOuts={onSaveEdgeWeightOverrides}
          edgeOverrideAmounts={form.watch("edgeOverrideAmounts") ?? []}
        />
      )}
      {isCustomRuleModalOpen && (
        <CustomAllocationRuleInputModal
          isOpen={isCustomRuleModalOpen}
          onModalClose={onCustomRuleModalClose}
          onSubmit={onSaveEdgeWeightOverrides}
          edgeOverrideAmounts={form.watch("edgeOverrideAmounts") ?? []}
        />
      )}
      <PageContent>
        <Form
          disableNavBlocker={true}
          form={form}
          onSubmit={onSubmit}
          onSaveProgress={toFundStructurePage}
        >
          <PageHeader
            title={t("allocation:ruleGraphEdit.title")}
            size="lg"
            workflowName={t("allocation:ruleGraphEdit.workflowName")}
          />
          <PageBody>
            <Flex w="100%" gap={12}>
              <Box flexGrow={2} minW="400px">
                <ContentBlockGroup>
                  <ContentBlock>
                    <ContentBlockBody>
                      <FormDateInput
                        name="effectiveDate"
                        title={t(
                          "allocation:ruleGraphEdit.effectiveDateOfChangePlaceholder",
                        )}
                      />
                      <FormTextArea
                        name="reasonForChange"
                        title={t(
                          "allocation:ruleGraphEdit.reasonForChangePlaceholder",
                        )}
                      />
                    </ContentBlockBody>
                  </ContentBlock>
                </ContentBlockGroup>
                <AllocationRuleGraphFormFields
                  fundFamilyId={fundFamilyId}
                  onAddOptOuts={onOptOutsModalOpen}
                  onAddCustomEdgeWeightOverrides={onCustomRuleModalOpen}
                  isOptOutsEnabled={!!ruleGraphData}
                  edgeOverrideAmounts={form.watch("edgeOverrideAmounts") ?? []}
                  isCustomRuleGraph={isCustomRuleGraph}
                />
              </Box>
              <AllocationBasisPreviewTable
                requiresRefresh={shouldRefresh}
                onRefresh={upsertDraftGraphAndUse}
                allocationRuleGraphVersionId={ruleGraphData?.graphVersionId}
                transactionCalculationId={form.watch("calculationId")}
                isCustomRuleGraph={isCustomRuleGraph}
              />
            </Flex>
          </PageBody>
          <FormFooter>
            <FormFooterBreadcrumb>
              <Breadcrumb
                separator={<ChevronRightIcon />}
                data-ls-enabled={true}
                data-public={true}
              >
                {[
                  t("allocation:ruleGraphEdit.editDetailsBreadcrumb"),
                  t("allocation:ruleGraphEdit.equalizationPreviewBreadcrumb"),
                ].map((title, index) => (
                  <BreadcrumbItem key={index} isCurrentPage={index === 0}>
                    <BreadcrumbLink aria-disabled={true}>
                      {title}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                ))}
              </Breadcrumb>
            </FormFooterBreadcrumb>
            <FormFooterActions>
              <FormCancelButton onClick={onCancel} />
              <FormSubmitButton
                isDisabled={
                  !(
                    ruleGraphData?.graphfundFamilyEventId &&
                    ruleGraphData.useFundFamilyEventId
                  )
                }
              >
                {t("allocation:ruleGraphEdit.continue")}
              </FormSubmitButton>
            </FormFooterActions>
          </FormFooter>
        </Form>
      </PageContent>
    </>
  );
};
